import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Upload } from 'lucide-react';
import { cn } from '../lib/cn';

// ─────────────────────────────────────────────────────────────────────────────
// ImageDropzone — the ONE drag-and-drop affordance every image upload site
// mounts (Lisa, 2026-07-19: "any images that need to be uploaded have the
// drag-and-drop option as an upload method — there seems to be a mix of
// methods at the moment.").
//
// Two modes, same component:
//
//  - wrap mode (children supplied) — layers onDragOver/onDrop over an
//    EXISTING picker control (e.g. a thumbnail + Upload/Replace button) so a
//    drop anywhere on that control hands the file to the SAME onFile the
//    click path already uses. Zero change to the existing control's layout
//    or validation — just a drag-over ring and a drop handler wired in.
//
//  - standalone mode (no children) — renders the house dashed-border zone
//    with click-to-browse baked in via its own hidden file input, for a
//    surface that has no existing picker UI to wrap.
//
// Either way this never invents a new upload path — onFile is handed
// straight to the caller's existing handler (the one that does real
// size/type validation and the actual presigned-PUT), exactly like the
// click-to-browse path already does.
// ─────────────────────────────────────────────────────────────────────────────

export interface ImageDropzoneProps {
  onFile: (file: File) => void;
  /** Comma-separated MIME list, mirrors the underlying file input's accept. */
  accept?: string;
  disabled?: boolean;
  className?: string;
  /** Wrap mode when supplied — see header comment. */
  children?: ReactNode;
  label?: string;
  hint?: string;
}

function acceptsFile(file: File, accept: string): boolean {
  const types = accept.split(',').map(t => t.trim()).filter(Boolean);
  if (types.length === 0) return file.type.startsWith('image/');
  return types.some(t => (t === 'image/*' ? file.type.startsWith('image/') : t === file.type));
}

export function ImageDropzone({
  onFile,
  accept = 'image/png,image/jpeg,image/webp',
  disabled = false,
  className,
  children,
  label = 'Drag an image here or click to browse',
  hint,
}: ImageDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!acceptsFile(file, accept)) {
      setRejected("That file isn't a supported image type.");
      setTimeout(() => setRejected(null), 3000);
      return;
    }
    setRejected(null);
    onFile(file);
  }

  function onDragOver(e: DragEvent) {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  }

  function onDragLeave() {
    setDragOver(false);
  }

  // Wrap mode — layer drag/drop over the caller's existing picker UI.
  if (children) {
    return (
      <div
        className={cn(
          'relative rounded transition-shadow',
          dragOver && !disabled && 'ring-2 ring-forge-500/70',
          className,
        )}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {children}
        {rejected && <p className="mt-1 text-[10px] text-red-400">{rejected}</p>}
      </div>
    );
  }

  // Standalone mode — the house dashed-border dropzone + click-to-browse.
  return (
    <div className={className}>
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={cn(
          'flex flex-col items-center justify-center gap-1.5 border border-dashed p-4 text-center transition-colors',
          disabled
            ? 'cursor-not-allowed border-iron-800 opacity-50'
            : 'cursor-pointer border-iron-700 hover:border-forge-500/60 hover:bg-iron-900/40',
          dragOver && !disabled && 'border-forge-500 bg-forge-500/10',
        )}
      >
        <Upload className="h-4 w-4 text-iron-500" strokeWidth={2} />
        <p className="text-[11px] text-iron-400">{label}</p>
        {hint && <p className="text-[10px] text-iron-500">{hint}</p>}
      </div>
      {rejected && <p className="mt-1 text-[10px] text-red-400">{rejected}</p>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={disabled}
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}
